import {
  Component,
  HostListener,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppStateService, ComposePrefill } from '../../../core/services/app-state.service';
import { IconComponent } from '../../components/icon/icon.component';
import { SIGNATURES, PEC_MAILBOXES } from '../../../core/data/mock-data';
import { Contact } from '../../../core/models/contact.model';
import { MailboxService } from '../../../../services/mailbox.service';
import { emailDestinatari, ICreateEmail } from '../../../../models/mail.model';
import { MailService } from '../../../../services/mail.service';
import { AllegatoS3 } from '../../../../models/allegati.model';
import { WebsocketService } from '../../../../services/websocket.service';
import { ContactsService, toContactView } from '../../../../services/contacts.service';
import { IContact } from '../../../../models/contact.model';
import { ListaDistribuzioneService } from '../../../../services/lista-distribuzione.service';
import { ListaDistribuzione } from '../../../../models/lista-distribuzione.model';
import { scaricaDaUrl } from '../../utils/utils';

function buildSmartIncipit(
  smart: { mode?: string; senderName?: string; subject?: string; category?: string } | null,
): string {
  if (!smart) return '';
  const rawName = (smart.senderName || '').trim();
  const subj = (smart.subject || '').replace(/^(re:|fwd:)\s*/i, '').trim();
  const cat = (smart.category || '').trim();
  if (smart.mode === 'reply') {
    const greet = rawName ? `Gentile ${rawName},` : 'Gentile mittente,';
    return `${greet}\n\nin riscontro alla Vostra comunicazione${subj ? ` avente ad oggetto «${subj}»` : ''}, Vi confermiamo la corretta ricezione e Vi trasmettiamo quanto segue.`;
  }
  if (smart.mode === 'forward') {
    return `Si trasmette per competenza la comunicazione${subj ? ` «${subj}»` : ''} pervenuta${rawName ? ` da ${rawName}` : ''}${cat ? `, classificata come «${cat}»` : ''}, per gli adempimenti di Vostra competenza.`;
  }
  return '';
}

@Component({
  selector: 'pec-compose-dialog',
  standalone: true,
  imports: [FormsModule, IconComponent],
  templateUrl: './compose.dialog.html',
  styleUrl: './compose.dialog.scss',
})
export class ComposeDialogComponent {
  @ViewChild('bodyTextarea') bodyTextareaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  readonly state = inject(AppStateService);
  readonly mailboxService = inject(MailboxService);
  readonly mailService = inject(MailService);
  readonly websocketService = inject(WebsocketService);
  readonly contactsService = inject(ContactsService);
  private readonly listaDistribuzioneService = inject(ListaDistribuzioneService);

  // `to`/`cc`/`ccn` sono il testo ancora in digitazione, non confermato: appena il campo perde
  // il focus (o si sceglie un suggerimento/contatto) il contenuto diventa un elemento in
  // `toChips`/`ccChips`/`ccnChips`, rimovibile singolarmente.
  to = '';
  cc = '';
  ccn = '';
  readonly toChips = signal<string[]>([]);
  readonly ccChips = signal<string[]>([]);
  readonly ccnChips = signal<string[]>([]);
  fromMailbox = '';
  subject = '';
  body = '';
  private selectedContact: { email: string; name: string } | null = null;

  readonly showCloseConfirm = signal(false);
  private initialSnapshot: {
    subject: string;
    body: string;
    to: string;
    cc: string;
    ccn: string;
    allegatiCount: number;
  } | null = null;

  readonly sending = signal(false);
  readonly sigId = signal('mr');
  readonly protocol = signal<{ num: string; date: string } | null>(null);
  readonly showAb = signal(false);
  readonly recipients = signal<Contact[]>([]);
  readonly smartDismissed = signal(false);
  readonly allegati = signal<AllegatoS3[]>([]);
  readonly abTab = signal<'contacts' | 'lists'>('contacts');
  readonly abSearch = signal('');
  readonly abSelectedIds = signal(new Set<string>());
  readonly abSelectedListIds = signal(new Set<number>());
  readonly allegatiOriginali = signal<{ nome: string; tipo: string, size?: number }[]>([]);

  readonly showCc = signal(false);

  /** Campo che la modale Rubrica compila: "A" dal pulsante Rubrica, "Cc" dalla lente del campo Cc. */
  readonly abTarget = signal<'to' | 'cc'>('to');

  // Rubrica dallo store condiviso (caricato al login): modale Rubrica e suggerimenti in A/Cc/Ccn
  // leggono gli stessi dati della pagina Rubrica, senza scaricarli di nuovo.
  readonly allContacts = computed<IContact[]>(() => this.contactsService.allContatti() ?? []);
  readonly contactsLoading = computed(() => this.contactsService.allContatti() === null);
  readonly activeSuggestField = signal<'to' | 'cc' | 'ccn' | null>(null);

  readonly mailboxes = PEC_MAILBOXES;

  // Rubrica reale dallo store condiviso (caricato al login), nel formato della vista: computed,
  // non una lettura una tantum, così segue i contatti caricati o creati dopo l'apertura.
  readonly contacts = computed<Contact[]>(() =>
    (this.contactsService.allContatti() ?? []).map(toContactView),
  );
  /** Liste di distribuzione reali (stesso store della pagina Rubrica). */
  readonly distributionLists = this.listaDistribuzioneService.listeDistribuzione;
  private readonly contactsById = computed(() => new Map(this.contacts().map(c => [c.id, c])));

  readonly suggestion = computed(() => {
    const smart = this.state.composePrefill()?.smart ?? null;
    return buildSmartIncipit(smart as Parameters<typeof buildSmartIncipit>[0]);
  });

  /**
   * Riepilogo del messaggio inoltrato, mostrato sotto il corpo in sola lettura. È solo visivo:
   * non entra in `body`, quindi non viene inviato né salvato in bozza.
   */
  readonly inoltro = computed(() => {
    const prefill = this.state.composePrefill();
    const m = prefill?.originale ?? (prefill?.mode === 'forward' ? prefill.mail : undefined);
    if (!m) return null;
    const corpo = m.corpo ?? '';
    const isHtml = /<\/?[a-z][^>]*>/i.test(corpo);
    return {
      titolo: prefill?.mode === 'forward' ? 'Inoltro del messaggio' : 'Messaggio originale',
      mittente: m.nomeMittente ? `${m.nomeMittente} <${m.mittente}>` : m.mittente,
      destinatario: emailDestinatari(m, 'TO').join(', '),
      oggetto: m.oggetto ?? '',
      // Il corpo HTML passa comunque dal sanitizer di Angular ([innerHTML]); qui si tolgono solo
      // gli stili inline (che il sanitizer scarterebbe con un warning) e i paragrafi vuoti, che
      // nell'anteprima diventerebbero righe bianche.
      corpoHtml: isHtml
        ? corpo
            .replace(/\sstyle="[^"]*"/gi, '')
            .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
        : null,
      corpoTesto: isHtml ? '' : corpo.trim(),
    };
  });

  readonly titleLabel = computed(() => {
    const mode = this.state.composePrefill()?.mode;
    if (mode === 'reply') return 'Rispondi';
    if (mode === 'forward') return 'Inoltra messaggio';
    return 'Nuovo messaggio PEC';
  });

  readonly filteredAbContacts = computed(() => {
    const q = this.abSearch().toLowerCase().trim();
    if (!q) return this.contacts();
    return this.contacts().filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q),
    );
  });

  get showGhost(): boolean {
    return !!this.suggestion() && this.body.trim() === '' && !this.smartDismissed();
  }

  /** Destinatari dell'inoltro: quelli scelti nel picker dedicato (inbox) se presenti,
   *  altrimenti i chip digitati nel campo "A:" (outbox/archive, dove non c'è un picker). */
  private get forwardTargets(): string[] {
    const explicit = this.state.composePrefill()?.forwardRecipients;
    if (explicit && explicit.length > 0) return explicit;
    return this.allEmails('to');
  }

  get canSend(): boolean {
    if (this.state.composePrefill()?.mode === 'forward') {
      return this.forwardTargets.length > 0;
    }
    return this.allEmails('to').length > 0 && this.subject.trim().length > 0;
  }

  get canSaveDraft(): boolean {
    return this.body.trim().length > 0;
  }

  get aiInsights() {
    return this.state.composePrefill()?.aiInsights ?? null;
  }

  constructor() {
    effect(
      () => {
        if (this.state.composeOpen()) {
          const prefill = this.state.composePrefill();
          this.to = '';
          this.cc = '';
          this.ccn = '';
          this.toChips.set(prefill?.to ? this.parseEmailList(prefill.to) : []);
          this.ccChips.set([]);
          this.ccnChips.set([]);
          this.showCc.set(false);
          this.selectedContact = null;
          const prefillFrom = prefill?.from;
          const matchedMailbox = prefillFrom
            ? this.mailboxService.mailboxes().find(mb => mb.email === prefillFrom)
            : null;
          this.fromMailbox = matchedMailbox?.email ?? this.mailboxService.mailboxes()[0].email;
          this.subject = prefill?.subject ?? '';
          this.body = prefill?.body ?? '';
          this.sigId.set('mr');
          this.protocol.set(null);
          this.showAb.set(false);
          this.recipients.set([]);
          this.smartDismissed.set(false);
          this.allegati.set([]);
          this.allegatiOriginali.set([]);
          this.loadAllegatiOriginali(prefill);
          this.abTab.set('contacts');
          this.abSearch.set('');
          this.abSelectedIds.set(new Set());
          this.abSelectedListIds.set(new Set());
          this.abTarget.set('to');
          this.activeSuggestField.set(null);
          this.showCloseConfirm.set(false);
          this.sending.set(false);
          this.ensureContacts();

          // Snapshot preso a fine reset: confrontato con lo stato corrente per capire se
          // l'utente ha effettivamente modificato qualcosa prima di chiedere conferma in uscita.
          // `untracked` è fondamentale qui: senza, la lettura di toChips/ccChips/ccnChips/allegati
          // registrerebbe quei signal come dipendenze di QUESTO effect (che li scrive anche, per
          // il reset) — ogni chip creato dopo l'apertura farebbe ripartire l'intero reset,
          // cancellando il badge appena aggiunto.
          this.initialSnapshot = untracked(() => ({
            subject: this.subject,
            body: this.body,
            to: this.allEmails('to').join(','),
            cc: this.allEmails('cc').join(','),
            ccn: this.allEmails('ccn').join(','),
            allegatiCount: this.allegati().length,
          }));
        }
      },
      { allowSignalWrites: true },
    );
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.showCloseConfirm()) {
      this.showCloseConfirm.set(false);
      return;
    }
    if (this.showAb()) {
      this.showAb.set(false);
      return;
    }
    if (this.state.composeOpen()) this.close();
  }

  onBodyKey(e: KeyboardEvent) {
    if (this.showGhost) {
      if (e.key === 'Tab' || (e.key === 'ArrowRight' && e.metaKey)) {
        e.preventDefault();
        this.acceptSuggestion();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.smartDismissed.set(true);
      }
    }
  }

  acceptSuggestion() {
    this.body = this.suggestion();
    this.smartDismissed.set(true);
    requestAnimationFrame(() => {
      const ta = this.bodyTextareaRef?.nativeElement;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
      }
    });
  }

  /** Apre la modale Rubrica per il campo indicato: "A" (pulsante Rubrica) o "Cc" (lente del campo Cc). */
  openAddressBook(target: 'to' | 'cc' = 'to') {
    // Rubrica e liste vengono di norma caricate altrove (login / pagina Rubrica): qui si
    // caricano solo se non sono ancora disponibili.
    this.ensureContacts();
    if (!this.listaDistribuzioneService.listeCaricate()) {
      this.listaDistribuzioneService.fetchListeDistribuzione();
    }
    this.abTarget.set(target);
    // Preselezionati i contatti di rubrica già presenti nel campo; gli altri indirizzi (scritti a
    // mano o membri di lista non in rubrica) non sono selezionabili qui e restano invariati.
    const byId = this.contactsById();
    this.abSelectedIds.set(new Set(this.chipsSignal(target)().filter(email => byId.has(email))));
    this.abSelectedListIds.set(new Set());
    this.abTab.set('contacts');
    this.abSearch.set('');
    this.showAb.set(true);
  }

  toggleAbContact(id: string) {
    const next = new Set(this.abSelectedIds());
    next.has(id) ? next.delete(id) : next.add(id);
    this.abSelectedIds.set(next);
  }

  toggleAbList(list: ListaDistribuzione) {
    const listIds = new Set(this.abSelectedListIds());
    const contactIds = new Set(this.abSelectedIds());
    const memberIds = this.getListMembers(list).map(m => m.id);
    if (listIds.has(list.id)) {
      listIds.delete(list.id);
      memberIds.forEach(cid => contactIds.delete(cid));
    } else {
      listIds.add(list.id);
      memberIds.forEach(cid => contactIds.add(cid));
    }
    this.abSelectedListIds.set(listIds);
    this.abSelectedIds.set(contactIds);
  }

  /**
   * Membri della lista nel formato della vista. L'id è l'email, come per i contatti della rubrica:
   * un membro presente in rubrica riusa quel contatto, altrimenti viene costruito dai dati della lista.
   */
  getListMembers(list: ListaDistribuzione): Contact[] {
    const byId = this.contactsById();
    return list.contatti.map(
      m => byId.get(m.email) ?? toContactView({ email: m.email, nome: m.nome, telefono: '', ragioneSociale: '' }),
    );
  }

  confirmAddressBook() {
    const field = this.abTarget();
    // I selezionati possono venire dalla rubrica o dai membri delle liste scelte, anche se non
    // presenti in rubrica: filtrare la sola rubrica li scarterebbe in silenzio.
    const pool = new Map(this.contactsById());
    this.distributionLists()
      .filter(l => this.abSelectedListIds().has(l.id))
      .flatMap(l => this.getListMembers(l))
      .forEach(m => pool.has(m.id) || pool.set(m.id, m));
    const selected = [...this.abSelectedIds()]
      .map(id => pool.get(id))
      .filter((c): c is Contact => !!c);
    // Gli indirizzi del campo che la Rubrica non gestisce restano: prima la conferma riscriveva
    // l'intero campo, cancellando quelli digitati a mano.
    const byId = this.contactsById();
    const daMantenere = this.chipsSignal(field)().filter(email => !byId.has(email));
    this.chipsSignal(field).set([...new Set([...daMantenere, ...selected.map(c => c.email)])]);
    this.setFieldValue(field, '');
    if (field === 'to') this.recipients.set(selected);
    this.showAb.set(false);
  }

  /** Carica la rubrica nello store condiviso solo se non è ancora disponibile. */
  private ensureContacts() {
    // `untracked`: viene chiamata anche dall'effect di apertura del compose, che non deve
    // dipendere dallo store dei contatti — altrimenti l'arrivo dei contatti rieseguirebbe
    // l'effect, azzerando l'intero form (stesso meccanismo del bug dei chip).
    if (untracked(() => this.contactsService.allContatti()) === null) {
      this.contactsService.setAll();
    }
  }

  private fieldValue(field: 'to' | 'cc' | 'ccn'): string {
    return field === 'to' ? this.to : field === 'cc' ? this.cc : this.ccn;
  }

  private setFieldValue(field: 'to' | 'cc' | 'ccn', value: string) {
    if (field === 'to') this.to = value;
    else if (field === 'cc') this.cc = value;
    else this.ccn = value;
  }

  private chipsSignal(field: 'to' | 'cc' | 'ccn') {
    return field === 'to' ? this.toChips : field === 'cc' ? this.ccChips : this.ccnChips;
  }

  /** Email confermate (chip) più l'eventuale testo ancora in digitazione, non ancora confermato. */
  private allEmails(field: 'to' | 'cc' | 'ccn'): string[] {
    const draft = this.fieldValue(field).trim();
    const chips = this.chipsSignal(field)();
    return draft && !chips.includes(draft) ? [...chips, draft] : chips;
  }

  /** Aggiunge un'email come elemento/chip e svuota il testo in digitazione per quel campo. */
  private addChip(field: 'to' | 'cc' | 'ccn', contact: IContact) {
    const chips = this.chipsSignal(field);
    if (!chips().includes(contact.email)) {
      chips.update(list => [...list, contact.email]);
    }
    this.setFieldValue(field, '');
    if (field === 'to') {
      this.selectedContact = contact.nome ? { email: contact.email, name: contact.nome } : null;
    }
  }

  removeChip(field: 'to' | 'cc' | 'ccn', email: string) {
    this.chipsSignal(field).update(list => list.filter(e => e !== email));
  }

  /** Appena il campo perde il focus, il testo digitato (se presente) diventa un elemento/chip. */
  onRecipientBlur(field: 'to' | 'cc' | 'ccn') {
    this.activeSuggestField.set(null);
    const value = this.fieldValue(field).trim();
    if (!value) return;
    const chips = this.chipsSignal(field);
    if (!chips().includes(value)) {
      chips.update(list => [...list, value]);
    }
    this.setFieldValue(field, '');
  }

  suggestionsFor(field: 'to' | 'cc' | 'ccn'): IContact[] {
    if (this.activeSuggestField() !== field) return [];
    const token = this.fieldValue(field).trim().toLowerCase();
    if (!token) return [];
    const chips = this.chipsSignal(field)();
    return this.allContacts()
      .filter(c => !chips.includes(c.email))
      .filter(
        c =>
          c.nome?.toLowerCase().includes(token) ||
          c.email.toLowerCase().includes(token) ||
          c.ragioneSociale?.toLowerCase().includes(token),
      )
      .slice(0, 6);
  }

  selectSuggestion(field: 'to' | 'cc' | 'ccn', contact: IContact) {
    this.addChip(field, contact);
    this.activeSuggestField.set(null);
  }

  contactInitials(nome: string, fallback: string): string {
    const parts = (nome ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (nome || fallback).substring(0, 2).toUpperCase();
  }

  /**
   * Punto di chiusura pubblico (X, backdrop, Escape): se qualcosa è stato modificato
   * (oggetto, corpo, allegati o destinatari) chiede conferma invece di chiudere subito.
   */
  close() {
    if (this.hasUnsavedChanges()) {
      this.showCloseConfirm.set(true);
      return;
    }
    this.dismiss();
  }

  hasUnsavedChanges(): boolean {
    const s = this.initialSnapshot;
    if (!s) return false;
    return (
      this.subject !== s.subject ||
      this.body !== s.body ||
      this.allEmails('to').join(',') !== s.to ||
      this.allEmails('cc').join(',') !== s.cc ||
      this.allEmails('ccn').join(',') !== s.ccn ||
      this.allegati().length !== s.allegatiCount
    );
  }

  discardAndClose() {
    this.showCloseConfirm.set(false);
    this.dismiss();
  }

  saveDraftAndClose() {
    this.showCloseConfirm.set(false);
    this.saveDraft();
  }

  private dismiss() {
    const mail = this.state.composePrefill()?.mail;
    if (mail) this.websocketService.unlockPec(mail);
    this.state.closeCompose();
  }

  triggerFileInput() {
    this.fileInputRef.nativeElement.click();
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    Array.from(input.files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        this.allegati.update(list => [
          ...list,
          { nome: file.name, contenuto: base64, tipo: file.type, size: file.size },
        ]);
      };
      reader.readAsDataURL(file);
    });
    input.value = '';
  }

  removeAllegato(index: number) {
    this.allegati.update(list => list.filter((_, i) => i !== index));
  }

  private parseEmailList(value: string): string[] {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }

  saveDraft() {
    const allegatiList = this.allegati();
    const prefill = this.state.composePrefill();
    const email: ICreateEmail = {
      dataComunicazione: new Date().toISOString(),
      mittente: this.fromMailbox,
      destinatari: this.allEmails('to'),
      cc: this.allEmails('cc'),
      oggetto: this.subject,
      corpo: this.body,
      priorita: 'STANDARD',
      tipo: 'PEC',
      allegati: allegatiList,
      // Solo per l'inoltro: una bozza di invio non ha un messaggio originale
      messaggioOriginaleId:
        prefill?.mode === 'forward' ? (prefill.mail ?? prefill.originale)?.id : undefined,
    };
    this.mailService.saveDraft(email).subscribe({
      next: () => {
        this.state.showToast(`Bozza salvata`);
        this.dismiss();
      },
      error: err => console.log(err),
    });
  }

  send() {
    if (!this.canSend || this.sending()) return;

    // L'inoltro è un'azione diversa dall'invio di una mail: non manda una vera email,
    // registra solo l'inoltro (endpoint /inoltro) per ciascun destinatario scelto.
    if (this.state.composePrefill()?.mode === 'forward') {
      this.sendForward();
      return;
    }

    const allegatiList = this.allegati();
    let totalSizeAttachments = 0;
    allegatiList.forEach(f => totalSizeAttachments += f.size ?? 0);

    if (totalSizeAttachments > (30 * 1024 * 1024)) {
      this.state.showToast("Il totale degli allegati non deve superare i 30 MB.", "error");
      return;
    }

    const draftId = this.state.composePrefill()?.draftId;
    if (draftId != null) {
      this.sending.set(true);
      this.mailService
        .sendDraftAsEmail(draftId, {
          dataComunicazione: new Date().toISOString(),
          mittente: this.fromMailbox,
          destinatari: this.allEmails('to'),
          cc: this.allEmails('cc'),
          ccn: this.allEmails('ccn'),
          oggetto: this.subject,
          corpo: this.body,
          priorita: 'STANDARD',
          tipo: 'OUTBOX',
          allegati: allegatiList,
        })
        .subscribe({
          next: () => {
            this.state.showToast(`Messaggio inviato`);
            this.dismiss();
          },
          error: err => {
            this.sending.set(false);
            console.log(err);
          },
        });
      return;
    }

    const email: ICreateEmail = {
      dataComunicazione: new Date().toISOString(),
      mittente: this.fromMailbox,
      destinatari: this.allEmails('to'),
      cc: this.allEmails('cc'),
      oggetto: this.subject,
      corpo: this.body,
      priorita: 'STANDARD',
      tipo: 'PEC',
      allegati: allegatiList,
    };

    this.sending.set(true);
    this.mailService.sendEmail(email).subscribe({
      next: () => {
        this.state.showToast(`Messaggio inviato`);
        this.dismiss();
      },
      error: err => {
        this.sending.set(false);
        console.log(err);
      },
    });
  }

  // Registra l'inoltro (nessuna email reale inviata): una chiamata a /inoltro per
  // ciascun destinatario scelto nella sezione Inoltri del dettaglio PEC.
  private sendForward() {
  const prefill = this.state.composePrefill();
  const recipients = this.forwardTargets;
  const mailId = prefill?.mail?.id ?? prefill?.originale?.id;
  if (!recipients.length || mailId == null) return;

    let totalSizeAttachments = 0;
    this.allegati().forEach(f => totalSizeAttachments += f.size ?? 0);
    this.allegatiOriginali().forEach(f => totalSizeAttachments += f.size ?? 0);

    if (totalSizeAttachments > (30 * 1024 * 1024)) {
      this.state.showToast("Il totale degli allegati non deve superare i 30 MB.", "error");
      return;
    }

    this.sending.set(true);
    this.mailService.inoltraMessaggio(
      mailId, recipients, this.body, this.fromMailbox,
      this.allegatiOriginali().map(a => a.nome),
      this.allegati()
    ).subscribe({
      next: () => {
        this.state.showToast('Inoltro registrato con successo');
        this.mailService.notifyMailUpdated(mailId);
        this.dismiss();
      },
      error: () => {
        this.sending.set(false);
        this.state.showToast("Si è verificato un errore durante l'inoltro");
      },
    })
}

  private loadAllegatiOriginali(prefill: ComposePrefill | null): void {
  const mailId = prefill?.mail?.id ?? prefill?.originale?.id;
  if (prefill?.mode !== 'forward' || mailId == null) return;
  this.mailService.getListaAllegati(mailId).subscribe({
    next: res => this.allegatiOriginali.set(res.allegati ?? []),
    error: err => console.error('[Compose] errore recupero allegati originali:', err),
  });
}

rimuoviAllegatoOriginale(nome: string): void {
  this.allegatiOriginali.update(list => list.filter(a => a.nome !== nome));
}

scaricaAllegatoOriginale(nome: string): void {
  const prefill = this.state.composePrefill();
  const mailId = prefill?.mail?.id ?? prefill?.originale?.id;
  if (mailId == null) return;
  this.mailService.getAllegatoUrl(mailId, nome, true).subscribe({
    next: doc => scaricaDaUrl(doc.url),
    error: () => this.state.showToast("Impossibile scaricare l'allegato", 'error'),
  });
}
}
